// CL6-F1-F3 (docs/build-briefs/CL6-F1-F3-timezone-required.md; DEC-281,
// DEC-286; design/mockups/cl6-f1-f3-timezone-required-*). The pure rules for
// the Settings "Business day" card (Location tab) and the floor refusal sheet,
// kept out of admin.ts and main.ts so they can be tested without a browser.
//
// The confirmation itself lives on the server (tenant_settings.
// business_day_confirmed_at). Nothing here decides whether a payment is
// refused; the server does. This only decides what a person sees.

export interface BusinessDayConfirmInput {
  /** ISO instant from GET .../settings, or null while unconfirmed. */
  confirmedAt: string | null;
  confirmedByName: string | null;
  /** The time zone stored on the server right now. */
  storedTimezone: string;
  /** The picker's current value; "" while its placeholder is showing. */
  pickedTimezone: string;
  /** The cutoff field's current value, "HH:MM" 24-hour, or "". */
  cutoff: string;
  /** Holds admin.location_settings.manage (the right PATCH .../settings needs). */
  canManage: boolean;
}

export interface BusinessDayConfirmView {
  confirmed: boolean;
  /** The warning card at the top of the Location tab. */
  showWarning: boolean;
  /** The "Needs setup" tag on the Location rail item. */
  showRailTag: boolean;
  cardTag: { text: string; tone: "warn" | "ok" };
  /** "Default, not confirmed" beside the time zone label. */
  showTimezoneTag: boolean;
  /** "Default, not confirmed" beside the cutoff label. */
  showCutoffTag: boolean;
  /** The picker shows "Pick your time zone" instead of a zone. */
  showTimezonePlaceholder: boolean;
  confirmButtonVisible: boolean;
  confirmButtonEnabled: boolean;
  /** Plain words beside a disabled Confirm; "" when enabled or hidden. */
  confirmDisabledReason: string;
  /** "This confirms Chicago, closing at 3:00 AM." once Confirm is live. */
  confirmLine: string;
  /** The normal "Save changes" button, only after confirming. */
  saveButtonVisible: boolean;
  /** "Changing either one later saves as normal. It stays confirmed." */
  confirmedNote: string;
  /** Shown instead of the buttons when the viewer cannot manage settings. */
  readOnlyLine: string;
  /** The warning card's last line: the way forward for a manager, the read-only
   * text for a viewer who cannot act on it. */
  warningNextLine: string;
  /** The pickers are disabled for a viewer who cannot change settings. */
  fieldsDisabled: boolean;
}

export const TIMEZONE_PLACEHOLDER_TEXT = "Pick your time zone";
export const DEFAULT_NOT_CONFIRMED_TEXT = "Default, not confirmed";
export const PICK_TIMEZONE_FIRST_TEXT = "Pick a time zone first.";
export const PICK_CUTOFF_FIRST_TEXT = "Pick a cutoff time first.";
export const READ_ONLY_CONFIRM_TEXT = "Ask someone with Settings access to confirm this.";
export const CHANGE_TO_SAVE_TEXT = "Change the time zone or cutoff to save.";
export const WARNING_NEXT_LINE_TEXT = "Pick your time zone, check the cutoff, then tap Confirm.";
export const CONFIRMED_NOTE_TEXT = "Changing either one later saves as normal. It stays confirmed.";
/** The zone the server stores before anyone has chosen one. */
export const DEFAULT_TIMEZONE = "UTC";

/** "Kerry Lebel" becomes "Kerry L."; a single word (or a role-like label such as
 * "Manager") is shown as it is, never with an invented initial. */
export function shortPersonName(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.length < 2) {
    return words[0] ?? "";
  }
  const first = words[0] ?? "";
  const last = words[words.length - 1] ?? "";
  if (/^[A-Za-z]\.$/.test(last)) {
    return `${first} ${last}`;
  }
  return `${first} ${last.charAt(0).toUpperCase()}.`;
}

/** "America/New_York" becomes "New York". */
export function timezoneCityName(zone: string): string {
  const last = zone.split("/").slice(-1)[0] ?? zone;
  return last.replace(/_/g, " ");
}

/** "03:00" becomes "3:00 AM", "15:30" becomes "3:30 PM". Anything that is not
 * a valid HH:MM comes back unchanged, so a bad value is never hidden. */
export function formatCutoffTwelveHour(cutoff: string): string {
  const match = /^(\d{2}):(\d{2})$/.exec(cutoff);
  if (match === null) {
    return cutoff;
  }
  const hour = Number(match[1]);
  const minute = match[2];
  if (hour > 23 || Number(minute) > 59) {
    return cutoff;
  }
  const suffix = hour < 12 ? "AM" : "PM";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${minute} ${suffix}`;
}

function formatConfirmedDate(iso: string, zone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
  try {
    return date.toLocaleDateString("en-US", { ...options, timeZone: zone });
  } catch {
    // An unrecognised zone name must not blank the tag; the date in UTC is
    // still an honest reading of when it was confirmed.
    return date.toLocaleDateString("en-US", { ...options, timeZone: "UTC" });
  }
}

export function businessDayConfirmView(input: BusinessDayConfirmInput): BusinessDayConfirmView {
  const confirmed = input.confirmedAt !== null;
  const timezoneReady = input.pickedTimezone.length > 0;
  const cutoffReady = input.cutoff.length > 0;
  const confirmReady = !confirmed && timezoneReady && cutoffReady;
  const showConfirmButton = !confirmed && input.canManage;

  let cardTag: BusinessDayConfirmView["cardTag"];
  if (input.confirmedAt !== null) {
    const when = formatConfirmedDate(input.confirmedAt, input.storedTimezone);
    cardTag = {
      text:
        input.confirmedByName === null
          ? `✓ Confirmed ${when}`
          : `✓ Confirmed ${when} by ${shortPersonName(input.confirmedByName)}`,
      tone: "ok",
    };
  } else {
    cardTag = { text: "Not confirmed", tone: "warn" };
  }

  let confirmDisabledReason = "";
  if (showConfirmButton && !confirmReady) {
    confirmDisabledReason = timezoneReady ? PICK_CUTOFF_FIRST_TEXT : PICK_TIMEZONE_FIRST_TEXT;
  }

  return {
    confirmed,
    showWarning: !confirmed,
    showRailTag: !confirmed,
    cardTag,
    showTimezoneTag: !confirmed && !timezoneReady,
    showCutoffTag: !confirmed,
    showTimezonePlaceholder: !confirmed && input.storedTimezone === DEFAULT_TIMEZONE,
    confirmButtonVisible: showConfirmButton,
    confirmButtonEnabled: showConfirmButton && confirmReady,
    confirmDisabledReason,
    confirmLine:
      showConfirmButton && confirmReady
        ? `This confirms ${timezoneCityName(input.pickedTimezone)}, closing at ${formatCutoffTwelveHour(input.cutoff)}.`
        : "",
    saveButtonVisible: confirmed && input.canManage,
    confirmedNote: confirmed && input.canManage ? CONFIRMED_NOTE_TEXT : "",
    readOnlyLine: !confirmed && !input.canManage ? READ_ONLY_CONFIRM_TEXT : "",
    warningNextLine: input.canManage ? WARNING_NEXT_LINE_TEXT : READ_ONLY_CONFIRM_TEXT,
    fieldsDisabled: !input.canManage,
  };
}

/** What the floor sheet says when a live payment is refused for this reason.
 * Open Settings is offered by permission (admin.location_settings.manage),
 * never by a role name; the caller resolves that permission. */
export interface BusinessDaySheetCopy {
  title: string;
  body: string;
  safeLine: string;
  nextStep: string;
  showOpenSettings: boolean;
}

export function businessDaySheetCopy(canOpenSettings: boolean): BusinessDaySheetCopy {
  return {
    title: "Card payments are turned off",
    body: "This location's time zone and closing time have not been confirmed yet, so the payment was stopped before it started.",
    safeLine: "No card was charged. The ticket is unchanged.",
    nextStep: canOpenSettings
      ? "Confirm them in Settings, under Location. Then tap Pay again."
      : "Ask a manager to confirm them in Settings, under Location. Then tap Pay again.",
    showOpenSettings: canOpenSettings,
  };
}
